import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowLeft,
  Building2,
  ClipboardList,
  ClipboardPlus,
  Eye,
  Globe,
  Megaphone,
  Mic,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  api,
  type AnnouncementAudience,
  type AnnouncementDetail,
  type AnnouncementRow,
  type Attachment,
  type DepartmentRef,
  type Paginated,
  type UserRef,
} from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatNumber, relativeTime } from '../lib/format';
import { AttachmentList, AttachmentsCard } from '../components/Attachments';
import { TaskHolderName } from '../components/TaskHolder';
import { isOpenUrgent } from '../lib/task-order';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  ProgressBar,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
} from '../components/ui';

type Tab = 'all' | 'unread' | 'mine';

const AUDIENCE: Record<AnnouncementAudience, { label: TranslationKey; icon: typeof Globe }> = {
  ALL: { label: 'announcements.audience.ALL', icon: Globe },
  USERS: { label: 'announcements.audience.USERS', icon: Users },
  DEPARTMENTS: { label: 'announcements.audience.DEPARTMENTS', icon: Building2 },
};

/**
 * Route: `/announcements` — news for the people in the system (operator's
 * request, 2026-10-03). Everyone reads what is for them, and anyone may post
 * one: administrators to anyone, everyone else (team leaders too, since
 * 2026-10-07) to their own departments. The tab lives in the URL (`?tab=unread`).
 */
export function AnnouncementsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const requested = params.get('tab');
  const tab: Tab = requested === 'unread' || requested === 'mine' ? requested : 'all';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['announcements', 'list', tab, page, q],
    queryFn: () =>
      api.get<Paginated<AnnouncementRow> & { unread: number }>('/announcements', { tab, page, pageSize: 20, q }),
  });

  const newButton = (
    <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/announcements/new')}>
      {t('announcements.new')}
    </Button>
  );
  const empty =
    tab === 'unread'
      ? { title: t('announcements.emptyUnread'), hint: t('announcements.emptyUnreadHint') }
      : tab === 'mine'
        ? { title: t('announcements.emptyMine'), hint: t('announcements.emptyHint') }
        : { title: t('announcements.empty'), hint: t('announcements.emptyHint') };

  return (
    <>
      <PageHeader
        title={t('announcements.title')}
        subtitle={t(can(P.ANNOUNCEMENTS_ANY_AUDIENCE) ? 'announcements.subtitle' : 'announcements.subtitleOwn')}
        count={data?.total}
        action={newButton}
      />

      <Tabs
        active={tab}
        onChange={(id) => {
          setPage(1);
          setParams(id === 'all' ? {} : { tab: id }, { replace: true });
        }}
        tabs={[
          { id: 'all', label: t('announcements.tab.all') },
          { id: 'unread', label: t('announcements.tab.unread'), count: data?.unread || undefined },
          { id: 'mine', label: t('announcements.tab.mine') },
        ]}
      />

      <Card padded={false} className="mt-5">
        <div className="border-b hairline p-4">
          <Input
            className="max-w-sm"
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Megaphone className="h-8 w-8" />}
            title={q ? undefined : empty.title}
            hint={q ? undefined : empty.hint}
            action={!q && tab !== 'unread' ? newButton : undefined}
          />
        ) : (
          <ul>
            {data.items.map((announcement) => (
              <AnnouncementItem key={announcement.id} announcement={announcement} />
            ))}
          </ul>
        )}
        {data && <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />}
      </Card>
    </>
  );
}

/**
 * One announcement in a list — the page's and the dashboard's. Unread ones are
 * marked; how it has landed (who opened it, the tasks made from it) shows only
 * to whoever keeps it.
 */
export function AnnouncementItem({ announcement: a }: { announcement: AnnouncementRow }) {
  const { t, locale, localized } = useI18n();
  const count = (n: number) => formatNumber(n, locale);
  return (
    <li className="border-b hairline last:border-0">
      <Link
        to={`/announcements/${a.id}`}
        className={clsx(
          'flex gap-3 px-5 py-3.5 transition-colors duration-fast hover:bg-[var(--surface-sunken)]',
          a.unread && 'bg-primary/5',
        )}
      >
        <span
          className={clsx(
            'relative mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full',
            a.unread ? 'bg-primary/15 text-primary-600 dark:text-primary-400' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]',
          )}
          aria-hidden
        >
          <Megaphone className="h-4 w-4 flip-rtl" />
          {a.unread && <span className="absolute -end-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-[var(--surface-card)]" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            {/* Typed in either language: it reads in its own direction, whatever the page's. */}
            <span
              className={clsx('min-w-0 truncate text-body text-[var(--text-primary)]', a.unread ? 'font-semibold' : 'font-medium')}
              dir="auto"
            >
              {a.title}
            </span>
            {a.unread && (
              <Badge tone="brand" className="shrink-0">
                {t('announcements.newBadge')}
              </Badge>
            )}
          </span>
          {a.excerpt && (
            <span className="mt-0.5 block truncate text-body-sm text-muted" dir="auto">
              {a.excerpt}
            </span>
          )}
          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-muted">
            <span className="font-medium text-[var(--text-secondary)]">{localized(a.createdBy)}</span>
            <span title={formatDateTime(a.createdAt, locale)}>{relativeTime(a.createdAt, locale)}</span>
            <AudienceLabel audience={a.audience} departments={a.departments} people={a.peopleCount} />
            {a.files > 0 && (
              <Meta icon={<Paperclip className="h-3.5 w-3.5" />} label={t('announcements.files')}>
                {count(a.files)}
              </Meta>
            )}
            {a.voiceNotes > 0 && (
              <Meta icon={<Mic className="h-3.5 w-3.5" />} label={t('announcements.voiceNotes')}>
                {count(a.voiceNotes)}
              </Meta>
            )}
            {a.seenCount !== null && (
              <Meta icon={<Eye className="h-3.5 w-3.5" />} label={t('announcements.seenBy')}>
                {count(a.seenCount)}
              </Meta>
            )}
            {!!a.taskCount && (
              <Meta icon={<ClipboardList className="h-3.5 w-3.5" />} label={t('announcements.tasksMade')}>
                {count(a.taskCount)}
              </Meta>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}

function Meta({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 tabular" title={label}>
      <span aria-hidden>{icon}</span>
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  );
}

/** "Everyone", "Selected people · 3", or the departments' names. */
function AudienceLabel({
  audience,
  departments,
  people,
}: {
  audience: AnnouncementAudience;
  departments: DepartmentRef[];
  people: number;
}) {
  const { t, locale, localized } = useI18n();
  const { icon: Icon, label } = AUDIENCE[audience];
  const text =
    audience === 'DEPARTMENTS' && departments.length
      ? departments.map((d) => localized(d)).join(locale === 'ar' ? '، ' : ', ')
      : audience === 'USERS'
        ? `${t(label)} · ${formatNumber(people, locale)}`
        : t(label);
  return (
    <span className="inline-flex min-w-0 items-center gap-1" title={t('announcements.audienceLabel')}>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{text}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// One announcement
// ---------------------------------------------------------------------------

const HISTORY: Record<string, TranslationKey> = {
  'announcement.created': 'announcements.history.created',
  'announcement.seen': 'announcements.history.seen',
  'announcement.updated': 'announcements.history.updated',
  'announcement.files_attached': 'announcements.history.filesAttached',
  'announcement.attachment_removed': 'announcements.history.attachmentRemoved',
  'announcement.task_created': 'announcements.history.taskCreated',
  'announcement.deleted': 'announcements.history.deleted',
};

const FIELD: Record<string, TranslationKey> = {
  title: 'announcements.field.title',
  body: 'announcements.field.body',
  audience: 'announcements.field.audience',
};

/**
 * Route: `/announcements/:id` — where the bell, the email and the dashboard
 * land. Opening it marks it seen. From here anyone who creates tasks makes one
 * from it; whoever posted it, and the admin, see who has seen it and its history.
 */
export function AnnouncementDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: a, isLoading, error, refetch } = useQuery({
    queryKey: ['announcement', id],
    queryFn: () => api.get<AnnouncementDetail>(`/announcements/${id}`),
  });

  // Opening it is what marks it seen, and a seen one leaves the dashboard: taken off
  // the cached card now, so going back never shows it again while the card refetches.
  const seen = a?.seenAt;
  useEffect(() => {
    if (!seen) return;
    queryClient.setQueryData(['dashboard'], (data: any) => {
      const card = data?.announcements;
      if (!card?.latest?.some((x: AnnouncementRow) => x.id === id)) return data;
      return {
        ...data,
        announcements: {
          unread: Math.max(0, card.unread - 1),
          latest: card.latest.filter((x: AnnouncementRow) => x.id !== id),
        },
      };
    });
    void queryClient.invalidateQueries({ queryKey: ['announcements'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [seen, id, queryClient]);

  if (isLoading) return <LoadingBlock />;
  if (error || !a) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const edited = new Date(a.updatedAt).getTime() - new Date(a.createdAt).getTime() > 60_000;
  const list = (names: string[]) => names.join(locale === 'ar' ? '، ' : ', ');

  return (
    <>
      <div className="mb-4">
        <Link to="/announcements" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('nav.announcements')}
        </Link>
      </div>

      <PageHeader
        title={<span dir="auto">{a.title}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t('announcements.postedBy', { name: localized(a.createdBy) })}</span>
            <span aria-hidden>·</span>
            <span title={formatDateTime(a.createdAt, locale)}>{relativeTime(a.createdAt, locale)}</span>
            {edited && (
              <>
                <span aria-hidden>·</span>
                <span title={formatDateTime(a.updatedAt, locale)}>
                  {t('announcements.edited', { when: relativeTime(a.updatedAt, locale) })}
                </span>
              </>
            )}
          </span>
        }
        action={
          <>
            {a.can.createTask && (
              <Button
                variant="primary"
                icon={<ClipboardPlus className="h-4 w-4" />}
                onClick={() => navigate(`/tasks/new?announcementId=${a.id}`)}
              >
                {t('announcements.createTask')}
              </Button>
            )}
            {a.can.edit && (
              <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/announcements/${a.id}/edit`)}>
                {t('common.edit')}
              </Button>
            )}
            {a.can.delete && (
              <Button icon={<Trash2 className="h-4 w-4" />} onClick={() => navigate(`/announcements/${a.id}/delete`)}>
                {t('common.delete')}
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <Card>
            <CardHeader title={t('announcements.details')} />
            <p className="whitespace-pre-wrap break-words text-body text-[var(--text-primary)]" dir="auto">
              {a.body || <span className="text-muted">{t('announcements.noDetails')}</span>}
            </p>
          </Card>

          {a.can.attach ? (
            <AttachmentsCard
              attachments={a.attachments}
              basePath={`/announcements/${a.id}`}
              queryKey={['announcement', a.id]}
              canRemove={(attachment: Attachment) => a.can.edit || attachment.file.uploadedBy?.id === user?.id}
            />
          ) : (
            a.attachments.length > 0 && (
              <Card>
                <CardHeader
                  title={t('attachments.title')}
                  action={<Badge tone="neutral" className="tabular">{a.attachments.length}</Badge>}
                />
                <AttachmentList attachments={a.attachments} />
              </Card>
            )
          )}

          <Card padded={false} className="min-w-0">
            <div className="p-5 pb-0">
              <CardHeader title={t('announcements.tasksTitle')} />
            </div>
            {a.tasks.length === 0 ? (
              <p className="px-5 pb-5 text-body-sm text-muted">{t('announcements.noTasks')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('common.assignee')}</Th>
                    <Th>{t('tasks.createdBy')}</Th>
                    <Th align="end">{t('common.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {a.tasks.map((task) => (
                    <Tr key={task.id} onClick={() => navigate(`/tasks/${task.id}`)}>
                      <Td className="max-w-xs">
                        {/* The API lists the open urgent ones first (2026-10-11); this says why. */}
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-medium" dir="auto">{task.title}</span>
                          {isOpenUrgent(task) && <Badge tone="error">{enumLabel('URGENT', locale)}</Badge>}
                        </span>
                        <span className="block text-caption text-muted tabular">{formatDateTime(task.createdAt, locale)}</span>
                      </Td>
                      <Td className="text-muted"><TaskHolderName task={task} empty={t('common.unassigned')} /></Td>
                      <Td className="text-muted">{localized(task.createdBy)}</Td>
                      <Td align="end">
                        <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader title={t('tickets.details')} />
            <dl>
              <Detail label={t('announcements.audienceLabel')}>
                {a.audience === 'ALL'
                  ? t('announcements.audience.ALL')
                  : a.audience === 'USERS'
                    ? list(a.users.map((p) => localized(p)))
                    : list(a.departments.map((d) => localized(d)))}
              </Detail>
              <Detail label={t('tasks.createdBy')}>{localized(a.createdBy)}</Detail>
              <Detail label={t('common.createdAt')}>
                <span className="tabular">{formatDateTime(a.createdAt, locale)}</span>
              </Detail>
              {edited && (
                <Detail label={t('common.updatedAt')}>
                  <span className="tabular">{formatDateTime(a.updatedAt, locale)}</span>
                </Detail>
              )}
            </dl>
          </Card>

          {a.reach && <ReachCard reach={a.reach} />}
          {a.history && <HistoryCard history={a.history} tasks={a.tasks} />}
        </div>
      </div>
    </>
  );
}

/** How many of the people it is for have opened it — who, when, and who has not yet. */
function ReachCard({ reach }: { reach: NonNullable<AnnouncementDetail['reach']> }) {
  const { t, locale, localized } = useI18n();
  const [allSeen, setAllSeen] = useState(false);
  const [allWaiting, setAllWaiting] = useState(false);
  const SHOWN = 8;
  const percent = reach.people > 0 ? (reach.seen / reach.people) * 100 : 0;
  const n = (value: number) => formatNumber(value, locale);
  const seenBy = allSeen ? reach.seenBy : reach.seenBy.slice(0, SHOWN);
  const notSeen = allWaiting ? reach.notSeen : reach.notSeen.slice(0, SHOWN);

  return (
    <Card>
      <CardHeader
        title={t('announcements.reachTitle')}
        action={
          <span className="text-body-sm font-medium text-[var(--text-secondary)] tabular">
            {t('announcements.reachOf', { seen: n(reach.seen), people: n(reach.people) })}
          </span>
        }
      />
      <ProgressBar percent={percent} tone={reach.people > 0 && reach.seen === reach.people ? 'success' : 'brand'} />
      {reach.people === 0 ? (
        <p className="mt-3 text-body-sm text-muted">{t('announcements.nobodyInAudience')}</p>
      ) : reach.seen === reach.people ? (
        <p className="mt-2 text-caption text-success-fg dark:text-success">{t('announcements.everyoneSaw')}</p>
      ) : reach.seen === 0 ? (
        <p className="mt-2 text-caption text-muted">{t('announcements.nobodySaw')}</p>
      ) : null}

      {reach.seenBy.length > 0 && (
        <section className="mt-4">
          <h4 className="mb-1.5 text-overline uppercase text-[var(--text-tertiary)]">
            {t('announcements.seenList')} · {n(reach.seenBy.length)}
          </h4>
          <ul className="space-y-1.5">
            {seenBy.map((row) => (
              <li key={row.user.id} className="flex items-baseline justify-between gap-3 text-body-sm">
                <span className="min-w-0 truncate text-[var(--text-primary)]">{localized(row.user)}</span>
                <span className="shrink-0 text-caption text-muted tabular" title={formatDateTime(row.seenAt, locale)}>
                  {formatDateTime(row.seenAt, locale)}
                </span>
              </li>
            ))}
          </ul>
          {reach.seenBy.length > SHOWN && (
            <ShowMore open={allSeen} total={reach.seenBy.length} onToggle={() => setAllSeen((v) => !v)} />
          )}
        </section>
      )}

      {reach.notSeen.length > 0 && (
        <section className="mt-4">
          <h4 className="mb-1.5 text-overline uppercase text-[var(--text-tertiary)]">
            {t('announcements.notSeenList')} · {n(reach.notSeen.length)}
          </h4>
          <ul className="flex flex-wrap gap-1.5">
            {notSeen.map((p: UserRef) => (
              <li
                key={p.id}
                className="rounded-pill bg-neutral-500/12 px-2.5 py-0.5 text-caption text-[var(--text-secondary)] ring-1 ring-inset ring-neutral-500/25"
              >
                {localized(p)}
              </li>
            ))}
          </ul>
          {reach.notSeen.length > SHOWN && (
            <ShowMore open={allWaiting} total={reach.notSeen.length} onToggle={() => setAllWaiting((v) => !v)} />
          )}
        </section>
      )}
    </Card>
  );
}

function ShowMore({ open, total, onToggle }: { open: boolean; total: number; onToggle: () => void }) {
  const { t, locale } = useI18n();
  return (
    <button type="button" onClick={onToggle} className="mt-2 text-caption font-medium text-primary-600 dark:text-primary-400 hover:underline">
      {open ? t('announcements.showLess') : t('announcements.showAll', { n: formatNumber(total, locale) })}
    </button>
  );
}

/** Everything done with it, newest first: posted, opened, edited, files, tasks made from it. */
function HistoryCard({
  history,
  tasks,
}: {
  history: NonNullable<AnnouncementDetail['history']>;
  tasks: AnnouncementDetail['tasks'];
}) {
  const { t, locale, localized } = useI18n();
  const [all, setAll] = useState(false);
  const SHOWN = 15;
  const openable = new Set(tasks.map((task) => task.id));
  const shown = all ? history : history.slice(0, SHOWN);
  const n = (value: number) => formatNumber(value, locale);

  const detailOf = (entry: (typeof history)[number]): ReactNode => {
    const meta = entry.metadata ?? {};
    switch (entry.action) {
      case 'announcement.created':
        return typeof meta.people === 'number' ? t('announcements.history.toldN', { n: n(meta.people) }) : null;
      case 'announcement.updated': {
        const fields = (Array.isArray(meta.changed) ? meta.changed : [])
          .map((field: string) => (FIELD[field] ? t(FIELD[field]) : field))
          .join(locale === 'ar' ? '، ' : ', ');
        return meta.told ? `${fields} · ${t('announcements.history.newlyToldN', { n: n(meta.told) })}` : fields;
      }
      case 'announcement.files_attached':
        return typeof meta.files === 'number' ? `(${n(meta.files)})` : null;
      case 'announcement.attachment_removed':
        return entry.message ? <bdi>{entry.message}</bdi> : null;
      case 'announcement.task_created':
        return (
          <>
            {meta.taskId && openable.has(meta.taskId) ? (
              <Link to={`/tasks/${meta.taskId}`} className="text-primary-600 dark:text-primary-400 hover:underline" dir="auto">
                {entry.message}
              </Link>
            ) : (
              <span dir="auto">{entry.message}</span>
            )}
            {meta.assignee && <> · {t('announcements.history.for', { name: localized(meta.assignee) })}</>}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader title={t('announcements.historyTitle')} />
      <ol className="space-y-3">
        {shown.map((entry) => {
          const detail = detailOf(entry);
          return (
            <li key={entry.id} className="flex gap-2.5">
              <span
                className={clsx(
                  'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                  entry.action === 'announcement.created' && 'bg-primary',
                  entry.action === 'announcement.task_created' && 'bg-success',
                  entry.action === 'announcement.seen' && 'bg-info',
                  !['announcement.created', 'announcement.task_created', 'announcement.seen'].includes(entry.action) &&
                    'bg-[var(--text-tertiary)]',
                )}
                aria-hidden
              />
              <div className="min-w-0 text-body-sm">
                <p className="text-[var(--text-primary)]">
                  <span className="font-medium">{entry.actor ? localized(entry.actor) : '—'}</span>
                  {' — '}
                  {HISTORY[entry.action] ? t(HISTORY[entry.action]) : entry.action}
                </p>
                {detail && <p className="break-words text-caption text-muted">{detail}</p>}
                <p className="text-caption text-faint tabular" title={formatDateTime(entry.occurredAt, locale)}>
                  {formatDateTime(entry.occurredAt, locale)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      {history.length > SHOWN && <ShowMore open={all} total={history.length} onToggle={() => setAll((v) => !v)} />}
    </Card>
  );
}
